// ── Readiness Badge ──
// Surfaces dispatch-lock state. Three states (Phase 1):
//   • "Not Ready"            — job.executionReady is unset
//   • "Ready for Execution"  — locked AND no recorded factor has regressed
//   • "Lock Stale"           — locked BUT at least one factor recorded in
//                              factorsAtLock no longer passes (signal only,
//                              no auto-invalidation, no admin override)
//
// Stale detection requires the canonical readiness inputs. When the badge
// is rendered without the `ctx` prop, it gracefully degrades to two-state
// behavior (Ready/Not Ready) — useful for low-info surfaces.
//
// Read-only — never wires to a mutation. Marking is done via the
// ReadinessGatingPanel CTA only.

import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Lock, CircleDashed, AlertTriangle } from "lucide-react";
import type { Job } from "@/data/types";
import { useAppData } from "@/data/AppDataContext";
import { getKit } from "@/data/materialKit";
import { isExecutionReady, isLockStale } from "@/data/executionReadyLock";

interface Props {
  job: Job;
  size?: "sm" | "md";
}

export function ReadinessBadge({ job, size = "sm" }: Props) {
  const { materialKits, payProfiles, contractors, buildReviewEvents } = useAppData();
  const ready = isExecutionReady(job);

  const stale = useMemo(() => {
    if (!ready) return false;
    const kit = getKit(materialKits, job.id);
    return isLockStale(job, { job, kit, payProfiles, contractors, buildReviewEvents });
  }, [ready, job, materialKits, payProfiles, contractors, buildReviewEvents]);

  const cls = size === "sm" ? "text-[10px] px-1.5 py-0" : "text-xs";

  // Precedence: stale > ready > not ready.
  if (stale) {
    return (
      <Badge
        variant="outline"
        className={`${cls} border-warning/40 bg-warning/10 text-warning gap-1`}
        title="A factor recorded at lock time no longer passes."
      >
        <AlertTriangle className="h-2.5 w-2.5" />
        Lock Stale
      </Badge>
    );
  }
  if (ready) {
    return (
      <Badge variant="outline" className={`${cls} border-success/40 bg-success/10 text-success gap-1`}>
        <Lock className="h-2.5 w-2.5" />
        Ready for Execution
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className={`${cls} border-muted-foreground/30 text-muted-foreground gap-1`}>
      <CircleDashed className="h-2.5 w-2.5" />
      Not Ready
    </Badge>
  );
}
